/**
 * SDK Override Parameters
 * 
 * Centralized parameter management for SDK behavior overrides.
 * Parameters can come from URL query params, localStorage, or cookies.
 * 
 * Key concepts:
 * - forceReset: Clears all stored state (localStorage, cookies) - simulates hard refresh
 * - draft: Marks customers/sessions as draft (for staging/preview/testing)
 * - funnelTracking: Controls whether funnel events are tracked
 * - tagadaClientEnv: Forces specific environment (production/development/local) - overrides auto-detection
 * - tagadaClientBaseUrl: Forces custom API base URL - overrides environment-based URL
 * - token: Authentication token (URL > localStorage)
 * - funnelSessionId: Active funnel session (URL > cookie)
 * 
 * ⚠️ Note: authCode is NOT handled here - it has highest priority and is handled
 * separately in client.ts before all other initialization logic.
 * 
 * Usage examples:
 * - Force production API: ?tagadaClientEnv=production
 * - Force development API: ?tagadaClientEnv=development
 * - Force local API: ?tagadaClientEnv=local
 * - Custom API URL: ?tagadaClientBaseUrl=https://tagada.loclx.io
 * - Combined: ?tagadaClientEnv=local&tagadaClientBaseUrl=https://tagada.loclx.io
 * - Hard reset + production: ?forceReset=true&tagadaClientEnv=production
 * - Cross-domain auth: ?authCode=ah_... (automatically handled, highest priority)
 */

import { clearClientToken, setClientToken, getClientToken } from './tokenStorage';
import { clearFunnelSessionCookie } from './sessionStorage';

/**
 * SDK Override Parameters - centralized across all SDK functions
 * Can be set via URL params, localStorage, or cookies
 */
export interface SDKOverrideParams {
    /** Force clean state (clear localStorage and cookies) - simulates hard refresh */
    forceReset?: boolean;
    /** Token from URL (overrides localStorage) */
    token?: string | null;
    /** Funnel session ID from URL (overrides cookie) */
    funnelSessionId?: string | null;
    /** Funnel ID from URL */
    funnelId?: string | null;
    /** Draft mode - marks customers/sessions as draft */
    draft?: boolean;
    /** Enable/disable funnel tracking events (false in config editor iframe, true in funnel previews) */
    funnelTracking?: boolean;
    /** Funnel environment (staging/production) - determines which funnel config to use */
    funnelEnv?: 'staging' | 'production';
    /** Force specific environment (production/development/local) - overrides auto-detection */
    tagadaClientEnv?: 'production' | 'development' | 'local';
    /** Force custom API base URL - overrides environment-based URL */
    tagadaClientBaseUrl?: string;
    /** Display currency override (e.g., 'USD', 'EUR') */
    currency?: string;
    /** Display locale override (e.g., 'en', 'fr') */
    locale?: string;
}

// Storage keys for SDK override params
const STORAGE_KEYS = {
    DRAFT: 'tgd_draft',
    FUNNEL_TRACKING: 'tgd_funnel_tracking',
    FORCE_RESET: 'tgd_force_reset',
    CLIENT_ENV: 'tgd_client_env',
    CLIENT_BASE_URL: 'tgd_client_base_url',
    CURRENCY: 'tgd_currency',
    LOCALE: 'tgd_locale',
} as const;

/**
 * Check if force reset is active (simulates hard refresh)
 */
export function isForceReset(): boolean {
    if (typeof window === 'undefined') return false;

    const params = new URLSearchParams(window.location.search);
    return params.get('forceReset') === 'true';
}

/**
 * Get value from localStorage with fallback
 */
function getFromStorage(key: string): string | null {
    if (typeof window === 'undefined') return null;
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
}

/**
 * Set value in localStorage
 */
function setInStorage(key: string, value: string): void {
    if (typeof window === 'undefined') return;
    try {
        localStorage.setItem(key, value);
    } catch {
        // Storage not available
    }
}

/**
 * Get value from cookie
 */
function getFromCookie(key: string): string | null {
    if (typeof document === 'undefined') return null;
    const cookies = document.cookie.split(';');
    const cookie = cookies.find(c => c.trim().startsWith(`${key}=`));
    return cookie ? cookie.split('=')[1]?.trim() : null;
}

/**
 * Set value in cookie
 * ⚠️ NEVER allows tgd_draft to be set as a cookie (localStorage-only)
 */
function setInCookie(key: string, value: string, maxAge = 86400): void {
    if (typeof document === 'undefined') return;

    // 🛡️ SAFETY: Block tgd_draft from EVER being written to cookies
    if (key === 'tgd_draft' || key === 'tgd-draft') {
        console.warn(`🛡️ [SDK] Blocked attempt to set ${key} as cookie - this should only be in localStorage`);
        return;
    }

    document.cookie = `${key}=${value}; path=/; max-age=${maxAge}`;
}

/**
 * Clear value from cookie
 */
function clearFromCookie(key: string): void {
    if (typeof document === 'undefined') return;
    document.cookie = `${key}=; path=/; max-age=0`;
}

/**
 * Get SDK override parameters from all sources (URL, localStorage, cookies)
 * Priority: URL > localStorage > Cookie
 */
export function getSDKParams(): SDKOverrideParams {
    if (typeof window === 'undefined') {
        return {};
    }

    const urlParams = new URLSearchParams(window.location.search);

    // Get draft mode (URL > localStorage only - no cookie fallback to avoid resurrection)
    let draft: boolean | undefined;
    const urlDraft = urlParams.get('draft');
    if (urlDraft !== null) {
        draft = urlDraft === 'true';
    } else {
        const storageDraft = getFromStorage(STORAGE_KEYS.DRAFT);
        if (storageDraft !== null) {
            draft = storageDraft === 'true';
        }
    }

    // Get funnel tracking (URL > localStorage > cookie, default true)
    let funnelTracking: boolean | undefined;
    const urlTracking = urlParams.get('funnelTracking');
    if (urlTracking !== null) {
        funnelTracking = urlTracking !== 'false';
    } else {
        const storageTracking = getFromStorage(STORAGE_KEYS.FUNNEL_TRACKING) || getFromCookie(STORAGE_KEYS.FUNNEL_TRACKING);
        if (storageTracking !== null) {
            funnelTracking = storageTracking !== 'false';
        }
    }

    // Get token (URL > localStorage)
    const token = urlParams.get('token') || getClientToken() || null;

    // Get funnel session ID (URL only, cookie handled elsewhere)
    const funnelSessionId = urlParams.get('funnelSessionId') || null;

    // Get funnel ID (URL only)
    const funnelId = urlParams.get('funnelId') || null;

    // Get funnel environment (URL only - not persisted, set on entry)
    let funnelEnv: 'staging' | 'production' | undefined;
    const urlFunnelEnv = urlParams.get('funnelEnv');
    if (urlFunnelEnv && (urlFunnelEnv === 'staging' || urlFunnelEnv === 'production')) {
        funnelEnv = urlFunnelEnv;
    }

    // Force reset is URL-only (not persisted)
    const forceReset = urlParams.get('forceReset') === 'true';

    // Get client environment override (URL > localStorage > cookie)
    let tagadaClientEnv: 'production' | 'development' | 'local' | undefined;
    const urlEnv = urlParams.get('tagadaClientEnv');
    if (urlEnv && (urlEnv === 'production' || urlEnv === 'development' || urlEnv === 'local')) {
        tagadaClientEnv = urlEnv;
    } else {
        const storageEnv = getFromStorage(STORAGE_KEYS.CLIENT_ENV) || getFromCookie(STORAGE_KEYS.CLIENT_ENV);
        if (storageEnv && (storageEnv === 'production' || storageEnv === 'development' || storageEnv === 'local')) {
            tagadaClientEnv = storageEnv as 'production' | 'development' | 'local';
        }
    }

    // Get custom API base URL override (URL > localStorage > cookie)
    let tagadaClientBaseUrl: string | undefined;
    const urlBaseUrl = urlParams.get('tagadaClientBaseUrl');
    if (urlBaseUrl) {
        tagadaClientBaseUrl = urlBaseUrl;
    } else {
        const storageBaseUrl = getFromStorage(STORAGE_KEYS.CLIENT_BASE_URL) || getFromCookie(STORAGE_KEYS.CLIENT_BASE_URL);
        if (storageBaseUrl) {
            tagadaClientBaseUrl = storageBaseUrl;
        }
    }

    // Get display currency (URL > localStorage > cookie)
    let currency: string | undefined;
    const urlCurrency = urlParams.get('currency');
    if (urlCurrency) {
        currency = urlCurrency;
    } else {
        const storageCurrency = getFromStorage(STORAGE_KEYS.CURRENCY) || getFromCookie(STORAGE_KEYS.CURRENCY);
        if (storageCurrency) {
            currency = storageCurrency;
        }
    }

    // Get display locale (URL > localStorage > cookie)
    let locale: string | undefined;
    const urlLocale = urlParams.get('locale');
    if (urlLocale) {
        locale = urlLocale;
    } else {
        const storageLocale = getFromStorage(STORAGE_KEYS.LOCALE) || getFromCookie(STORAGE_KEYS.LOCALE);
        if (storageLocale) {
            locale = storageLocale;
        }
    }

    return {
        forceReset,
        token,
        funnelSessionId,
        funnelId,
        draft,
        funnelTracking,
        funnelEnv,
        tagadaClientEnv,
        tagadaClientBaseUrl,
        currency,
        locale,
    };
}

/**
 * Legacy alias for backward compatibility
 * @deprecated Use getSDKParams() instead
 */
export function getPreviewParams(): SDKOverrideParams {
    return getSDKParams();
}

/**
 * Check if draft mode is active
 * Uses centralized getSDKParams()
 */
export function isDraftMode(): boolean {
    const params = getSDKParams();
    return params.draft ?? false;
}

/**
 * Set draft mode in storage for persistence
 * ⚠️ ONLY writes to localStorage (not cookies) to avoid resurrection issues
 */
export function setDraftMode(draft: boolean): void {
    if (typeof window === 'undefined') return;

    if (draft) {
        setInStorage(STORAGE_KEYS.DRAFT, 'true');
    } else {
        try {
            localStorage.removeItem(STORAGE_KEYS.DRAFT);
        } catch {
            // Storage not available
        }
    }
}

/**
 * Initialize SDK with override parameters
 * 
 * This function handles SDK initialization based on override params:
 * 1. If forceReset flag is set: Clear all stored state (simulates hard refresh)
 * 2. If token in URL: Use it (override localStorage)
 * 3. If draft in URL: Persist it to storage
 * 4. If funnelTracking in URL: Persist it to storage
 * 
 * @returns True if force reset was activated and state was cleared
 */
export function handlePreviewMode(debugMode = false): boolean {
    // 🔒 CRITICAL: Read URL token DIRECTLY first before any getSDKParams()
    // This ensures we have the URL token before any clearing happens
    let urlToken: string | null = null;
    if (typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        urlToken = urlParams.get('token');
    }

    const params = getSDKParams();
    const shouldReset = params.forceReset || false;

    if (!shouldReset && !params.token) {
        // Not forcing reset and no explicit token override
        // But still persist draft/tracking if in URL
        persistSDKParamsFromURL();
        return false;
    }

    if (debugMode) {
        console.log('[SDK] Detected params:', params);
        console.log('[SDK] URL token (direct read):', urlToken ? urlToken.substring(0, 20) + '...' : 'none');
    }

    // CASE 1: Force reset - clear all state (simulates hard refresh)
    if (shouldReset) {
        if (debugMode) {
            console.log('[SDK] Force reset: Clearing all stored state');
        }

        clearClientToken();
        clearFunnelSessionCookie();

        // Clear all tagadapay-related localStorage keys
        if (typeof window !== 'undefined' && window.localStorage) {
            const keys = Object.keys(localStorage);
            keys.forEach(key => {
                if (key.startsWith('tagadapay_') || key.startsWith('tgd_')) {
                    if (debugMode) {
                        console.log(`[SDK] Clearing localStorage: ${key}`);
                    }
                    localStorage.removeItem(key);
                }
            });
        }
    }

    // CASE 2: Token in URL - override stored token
    // 🔒 CRITICAL: Use the directly-read URL token to avoid any race conditions
    const tokenToSet = urlToken || params.token;
    if (tokenToSet !== null && tokenToSet !== undefined) {
        if (debugMode) {
            console.log('[SDK] Setting token from URL:', tokenToSet.substring(0, 20) + '...');
        }

        if (tokenToSet === '' || tokenToSet === 'null') {
            // Explicitly cleared token
            clearClientToken();
        } else {
            // Set token from URL IMMEDIATELY after clearing
            setClientToken(tokenToSet);
            if (debugMode) {
                console.log('[SDK] ✅ Token set in localStorage immediately after clear');
            }
        }
    } else if (shouldReset) {
        // Force reset but no token = clear stored token
        if (debugMode) {
            console.log('[SDK] Force reset mode (no token in URL)');
        }
        clearClientToken();
    }

    // CASE 3: Persist URL params to storage
    persistSDKParamsFromURL();

    // CASE 4: Funnel session ID in URL - will be used by FunnelClient
    if (params.funnelSessionId && debugMode) {
        console.log('[SDK] Using funnelSessionId from URL:', params.funnelSessionId);
    }

    return shouldReset;
}

/**
 * Persist SDK parameters from URL to storage
 * This ensures params survive page navigation
 */
function persistSDKParamsFromURL(): void {
    if (typeof window === 'undefined') return;

    const urlParams = new URLSearchParams(window.location.search);

    // Persist draft mode if in URL
    const urlDraft = urlParams.get('draft');
    if (urlDraft !== null) {
        setDraftMode(urlDraft === 'true');
    }

    // Persist funnel tracking if in URL
    const urlTracking = urlParams.get('funnelTracking');
    if (urlTracking !== null) {
        setFunnelTracking(urlTracking !== 'false');
    }

    // Persist client environment if in URL
    const urlEnv = urlParams.get('tagadaClientEnv');
    if (urlEnv && (urlEnv === 'production' || urlEnv === 'development' || urlEnv === 'local')) {
        setClientEnvironment(urlEnv);
    }

    // Persist custom base URL if in URL
    const urlBaseUrl = urlParams.get('tagadaClientBaseUrl');
    if (urlBaseUrl) {
        setClientBaseUrl(urlBaseUrl);
    }

    // Persist currency if in URL (survives navigation between funnel steps)
    const urlCurrency = urlParams.get('currency');
    if (urlCurrency) {
        setInStorage(STORAGE_KEYS.CURRENCY, urlCurrency.toUpperCase());
        setInCookie(STORAGE_KEYS.CURRENCY, urlCurrency.toUpperCase(), 86400);
    }

    // Persist locale if in URL
    const urlLocale = urlParams.get('locale');
    if (urlLocale) {
        setInStorage(STORAGE_KEYS.LOCALE, urlLocale);
        setInCookie(STORAGE_KEYS.LOCALE, urlLocale, 86400);
    }
}

/**
 * Set funnel tracking mode in storage for persistence
 */
export function setFunnelTracking(enabled: boolean): void {
    if (typeof window === 'undefined') return;

    const value = enabled ? 'true' : 'false';
    setInStorage(STORAGE_KEYS.FUNNEL_TRACKING, value);
    setInCookie(STORAGE_KEYS.FUNNEL_TRACKING, value, 86400);
}

/**
 * Set client environment override in storage for persistence
 */
export function setClientEnvironment(env: 'production' | 'development' | 'local'): void {
    if (typeof window === 'undefined') return;

    setInStorage(STORAGE_KEYS.CLIENT_ENV, env);
    setInCookie(STORAGE_KEYS.CLIENT_ENV, env, 86400);
}

/**
 * Clear client environment override
 */
export function clearClientEnvironment(): void {
    if (typeof window === 'undefined') return;

    try {
        localStorage.removeItem(STORAGE_KEYS.CLIENT_ENV);
        clearFromCookie(STORAGE_KEYS.CLIENT_ENV);
    } catch {
        // Storage not available
    }
}

/**
 * Set custom API base URL override in storage for persistence
 */
export function setClientBaseUrl(baseUrl: string): void {
    if (typeof window === 'undefined') return;

    setInStorage(STORAGE_KEYS.CLIENT_BASE_URL, baseUrl);
    setInCookie(STORAGE_KEYS.CLIENT_BASE_URL, baseUrl, 86400);
}

/**
 * Clear custom API base URL override
 */
export function clearClientBaseUrl(): void {
    if (typeof window === 'undefined') return;

    try {
        localStorage.removeItem(STORAGE_KEYS.CLIENT_BASE_URL);
        clearFromCookie(STORAGE_KEYS.CLIENT_BASE_URL);
    } catch {
        // Storage not available
    }
}

/**
 * Check if we should use URL params over stored values
 * 
 * Returns true if:
 * - Force reset is active, OR
 * - Explicit token/sessionId in URL (indicating intentional override)
 */
export function shouldUseUrlParams(): boolean {
    const params = getSDKParams();
    return !!(
        params.forceReset ||
        params.token !== null ||
        params.funnelSessionId !== null
    );
}

/**
 * Check if funnel tracking is enabled
 * Uses centralized getSDKParams()
 * Default is true for normal operations
 */
export function isFunnelTrackingEnabled(): boolean {
    const params = getSDKParams();
    return params.funnelTracking ?? true; // Default true
}

